import { useEffect, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { api } from '../../services/api';

ChartJS.register(ArcElement, Tooltip, Legend);

type Props = {
  annee: number;
  mois?: number;
};

type Entry = {
  libelle: string;
  total: number;
};

export default function NbBateauxChart({ annee, mois }: Props) {
  const [contrats, setContrats] = useState<Entry[]>([]);
  const [passages, setPassages] = useState<Entry[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true); // ⬅️ Active le loading au début
        const res = await api.get('/nb-bateaux', {
          params: {
            annee,
            ...(mois && { mois }),
          },
        });
        setContrats(res.data.contrats || []);
        setPassages(res.data.passages || []);
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Erreur de chargement');
      } finally {
        setLoading(false); // ⬅️ Désactive le loading une fois fini
      }
    };

    fetchData();
  }, [annee, mois]);

  const colors = [
    '#FF6384','#36A2EB','#FFCE56','#4BC0C0','#9966FF','#FF9F40',
    '#10B981','#F472B6','#6366F1','#F59E0B','#06B6D4','#A855F7',
    '#EF4444','#3B82F6','#8B5CF6','#14B8A6','#F43F5E','#EAB308'
  ];

  const contratsChartData = {
    labels: contrats.map(c => c.libelle),
    datasets: [
      {
        label: 'Contrats',
        data: contrats.map(c => c.total),
        backgroundColor: colors.map(c => c + '80'),
        borderColor: '#FFFFFF',
        borderWidth: 0.5,
      },
    ],
  };

  const globalChartData = {
    labels: ['Contrats', 'Passages'],
    datasets: [
      {
        label: 'Total',
        data: [
          contrats.reduce((sum, c) => sum + c.total, 0),
          passages.reduce((sum, p) => sum + p.total, 0),
        ],
        backgroundColor: ['#4f46e580', '#22c55e80'],
        borderColor: '#FFFFFF',
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-5 pb-4 pt-5 dark:border-darkBorderCard dark:bg-white/[0.03] sm:px-6 sm:pt-6">
      <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Répartition des bateaux</h2> 

      {/* Affiche le message de chargement tant que loading = true */}
      {loading && <div className="text-gray-500 my-4">Chargement…</div>}

      {/* Si pas d'erreur et pas en chargement, on affiche les graph */}
      {!loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          <div>
            <h3 className="text-center text-sm font-medium mb-2 text-gray-600">Contrats par type</h3>
            <Pie data={contratsChartData} />
          </div>
          <div>
            <h3 className="text-center text-sm font-medium mb-2 text-gray-600">Contrats vs Passages</h3>
            <Pie data={globalChartData} />
          </div>
        </div>
      )}

      {/* Affiche l'erreur si présente */}
      {error && !loading && <div className="text-red-500 mt-4">{error}</div>}
    </div>
  );
}
